import { Controller } from "@hotwired/stimulus";

/**
 * MediaBrowserSelection Controller
 *
 * Handles image selection state in the media browser grid.
 * Manages single-select mode with visual feedback and details panel.
 */
export default class extends Controller {
  static targets = ["detailsPanel", "selectButton", "checkmark"];
  static values = {
    selectedBlobId: String,
  };

  connect() {
    this.selectedBlobId = null;
    console.log("MediaBrowserSelection controller connected");
  }

  /**
   * Handle tile click to select/deselect image
   * Only one image can be selected at a time (single-select mode)
   */
  selectTile(event) {
    const tile = event.currentTarget;
    const blobId = tile.dataset.blobId;

    // If clicking already selected tile, deselect it
    if (this.selectedBlobId === blobId) {
      this.deselectAll();
      return;
    }

    // Deselect all tiles first
    this.deselectAll();

    // Select the clicked tile
    tile.classList.add("ring-4", "ring-fmt-gold", "border-fmt-gold");
    this.selectedBlobId = blobId;

    // Show details panel with image metadata
    this.showDetails({
      filename: tile.dataset.filename,
      fileSize: tile.dataset.fileSize,
      dimensions: tile.dataset.dimensions,
      uploadDate: tile.dataset.uploadDate,
      thumbnailUrl: tile.dataset.thumbnailUrl,
      blobId: blobId,
    });

    // Enable select button
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = false;
    }

    // Announce selection to screen readers
    this.announceSelection(tile.dataset.filename);
  }

  /**
   * Deselect all image tiles
   */
  deselectAll() {
    // Remove selection styling from all tiles
    const allTiles = this.element.querySelectorAll("[data-media-browser-tile]");
    allTiles.forEach((tile) => {
      tile.classList.remove("ring-4", "ring-fmt-gold", "border-fmt-gold");
    });

    // Clear selected blob ID
    this.selectedBlobId = null;

    // Hide details panel
    if (this.hasDetailsPanelTarget) {
      this.detailsPanelTarget.classList.add("hidden");
      this.detailsPanelTarget.innerHTML = "";
    }

    // Disable select button
    if (this.hasSelectButtonTarget) {
      this.selectButtonTarget.disabled = true;
    }
  }

  /**
   * Show details panel with selected image metadata
   * Uses DOM APIs to prevent XSS from untrusted data attributes
   */
  showDetails(data) {
    if (!this.hasDetailsPanelTarget) return;

    // Create main container
    const container = document.createElement("div");
    container.className =
      "bg-gray-50 dark:bg-neutral-900 rounded-lg p-4 border border-gray-200 dark:border-neutral-700";

    // Create heading
    const heading = document.createElement("h3");
    heading.className =
      "text-sm font-medium text-neutral-900 dark:text-white mb-3";
    heading.textContent = "Selected Image Details";
    container.appendChild(heading);

    // Create flex container
    const flexContainer = document.createElement("div");
    flexContainer.className = "flex gap-4";

    // Create image container
    const imageContainer = document.createElement("div");
    imageContainer.className = "flex-shrink-0";

    const img = document.createElement("img");
    img.src = data.thumbnailUrl; // Safe: uses property assignment
    img.alt = data.filename; // Safe: uses property assignment
    img.className =
      "w-24 h-24 object-cover rounded-lg border border-gray-200 dark:border-neutral-700";
    imageContainer.appendChild(img);
    flexContainer.appendChild(imageContainer);

    // Create details container
    const detailsContainer = document.createElement("div");
    detailsContainer.className = "flex-1 min-w-0";

    const dl = document.createElement("dl");
    dl.className = "space-y-2";

    // Helper to create a detail row
    const createDetailRow = (label, value, extraClasses = "") => {
      const div = document.createElement("div");
      if (extraClasses) div.className = extraClasses;

      const dt = document.createElement("dt");
      dt.className =
        "text-xs font-medium text-neutral-500 dark:text-neutral-400";
      dt.textContent = label;
      div.appendChild(dt);

      const dd = document.createElement("dd");
      dd.className = "text-sm text-neutral-900 dark:text-white";
      dd.textContent = value; // Safe: uses textContent
      div.appendChild(dd);

      return div;
    };

    // Filename (with truncation and title)
    const filenameDiv = document.createElement("div");
    const filenameDt = document.createElement("dt");
    filenameDt.className =
      "text-xs font-medium text-neutral-500 dark:text-neutral-400";
    filenameDt.textContent = "Filename";
    filenameDiv.appendChild(filenameDt);

    const filenameDd = document.createElement("dd");
    filenameDd.className = "text-sm text-neutral-900 dark:text-white truncate";
    filenameDd.title = data.filename; // Safe: uses property assignment
    filenameDd.textContent = data.filename; // Safe: uses textContent
    filenameDiv.appendChild(filenameDd);
    dl.appendChild(filenameDiv);

    // Size and Dimensions in grid
    const gridDiv = document.createElement("div");
    gridDiv.className = "grid grid-cols-2 gap-4";
    gridDiv.appendChild(createDetailRow("Size", data.fileSize));
    gridDiv.appendChild(createDetailRow("Dimensions", data.dimensions));
    dl.appendChild(gridDiv);

    // Uploaded date
    dl.appendChild(createDetailRow("Uploaded", data.uploadDate));

    detailsContainer.appendChild(dl);
    flexContainer.appendChild(detailsContainer);
    container.appendChild(flexContainer);

    // Replace panel contents
    this.detailsPanelTarget.replaceChildren(container);
    this.detailsPanelTarget.classList.remove("hidden");

    // Scroll details panel into view smoothly
    this.detailsPanelTarget.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  /**
   * Announce selection to screen readers for accessibility
   */
  announceSelection(filename) {
    const announcement = `Selected image: ${filename}`;

    // Create or update live region for screen reader announcements
    let liveRegion = document.getElementById("media-browser-announcements");

    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "media-browser-announcements";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = announcement;
  }

  /**
   * Get the currently selected blob ID
   * Used by submit controller to attach the image
   */
  getSelectedBlobId() {
    return this.selectedBlobId;
  }

  /**
   * Get the currently selected image URL
   * Used by BlockNote integration to get the image URL directly
   */
  getSelectedImageUrl() {
    if (!this.selectedBlobId) return null;

    const selectedTile = this.element.querySelector(
      `[data-blob-id="${this.selectedBlobId}"]`,
    );
    return selectedTile ? selectedTile.dataset.thumbnailUrl : null;
  }
}
